import PieChart from "common/Charts/PieChart/PieChart";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import { BacktestingScoreBreakdown, LanguageNameSpaces } from "@shared/types";
import useRiskChart from "@shared/features/newMarketplace/hooks/useRiskChart";
import useMedia from "utils/hooks/useMedia";
import { TABLET } from "common/constants";
import { useMemo, useRef } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface RiskChartProps {
  scoreBreakdown: BacktestingScoreBreakdown;
}

const RiskChart = ({ scoreBreakdown }: RiskChartProps) => {
  const isSmallScreen = useMedia([`(max-width: ${TABLET})`], [true], false);
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const { chartData, riskValue, riskType, riskTextColor, riskTypeColor } =
    useRiskChart({
      scoreBreakdown,
    });
  const theme = useTheme();
  const themeRef = useRef(theme);
  themeRef.current = theme;

  const pointPlugin = useMemo(
    () => ({
      id: "pointPlugin",
      afterDraw: function (chart: any) {
        const { ctx } = chart;
        const data = chart._metasets[0].data[0];
        const radius =
          data.innerRadius + (data.outerRadius - data.innerRadius) / 2;
        const centerX = data.x;
        const centerY = data.y;
        const angle = Math.PI * (riskValue / 100) + Math.PI;
        const x = centerX + radius * Math.cos(angle);
        const y = centerY + radius * Math.sin(angle);

        ctx.save();
        ctx.beginPath();
        ctx.lineWidth = 2;
        ctx.strokeStyle = riskTypeColor;
        ctx.fillStyle = themeRef.current.colors.cardBgColor;
        ctx.arc(x, y, 5, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      },
    }),
    [riskValue]
  );

  return (
    <Flex $flexDirection="column">
      <PieChart
        key={`risk_chart_${riskValue.toString()}`}
        data={chartData}
        halfChart={true}
        hideTotal={true}
        chartSize={isSmallScreen ? 70 : 80}
        chartWeight={"82%"}
        chartParentElementId="riskPieChart"
        extraInfoStyle={EXTRA_INFO_STYLE}
        chartStyle={CHART_STYLE}
        hideInnerCircle={true}
        hideTooltip={true}
        plugin={pointPlugin as any}
        borderWidth={2}
        disableHover={true}
        wrapperStyle={{ margin: 0 }}
      />
      {riskType && <Risk color={riskTextColor}>{t(riskType)}</Risk>}
    </Flex>
  );
};

export default RiskChart;

const CHART_STYLE = { margin: 0, flex: "none", transform: "translateY(2px)" };
const EXTRA_INFO_STYLE = { margin: 0, display: "none" };

const Risk = styled(Text)`
  transform: translateY(-2px);
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
